import { useState } from 'react';
import { memoriasDePersonaAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Brain, Loader2, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * Lo que el área sabe de una persona, con una fila por cápsula.
 *
 * **Una fila por cápsula y no un switch global** (decisión de Diego, 2026-08-29): la memoria es
 * por área, y alguien que trabaja en sistemas —donde contesta— puede tener memoria en RRHH,
 * donde pide. Un solo interruptor obligaría a elegir entre las dos.
 *
 * **Se carga al abrir, no con la lista.** Son doscientas personas en pantalla; pedir la memoria
 * de todas para mostrar un renglón sería doscientas consultas para leer una.
 */
export default function MemoriasDeLaPersona({ tenantId, contactId, nombre }) {
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [memorias, setMemorias] = useState([]);
  const [editando, setEditando] = useState(null);
  const [borrador, setBorrador] = useState({ comportamiento: '', prosa_del_area: '' });
  const [guardando, setGuardando] = useState(false);
  const [confirmacion, setConfirmacion] = useState({ open: false });
  const [hechosAbiertos, setHechosAbiertos] = useState({});

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await memoriasDePersonaAPI.deLaPersona(tenantId, contactId);
      setMemorias(r.data?.memorias || []);
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudieron leer las memorias'));
    } finally {
      setCargando(false);
    }
  };

  const alternar = async () => {
    const proximo = !abierto;
    setAbierto(proximo);
    if (proximo && !memorias.length) await cargar();
  };

  /**
   * Saca un hecho. **Es la única forma de corregir lo que el sistema aprendió solo.**
   *
   * El párrafo de "aprendido de sus casos" no se edita a mano porque es un derivado: la
   * siguiente regeneración lo volvería a escribir igual. Lo que se corrige es su fuente.
   */
  const borrarHecho = async (m, hecho) => {
    try {
      await memoriasDePersonaAPI.borrarHecho(tenantId, m.capsule_id, contactId, hecho.id);
      toast.success('Hecho quitado. El párrafo se reescribe en el próximo caso que se cierre.');
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo quitar el hecho'));
    }
  };

  const cambiarInterruptor = async (m, activa) => {
    try {
      await memoriasDePersonaAPI.interruptor(tenantId, m.capsule_id, contactId, activa);
      toast.success(activa
        ? `Los agentes de ${m.capsule_id} vuelven a usarla`
        : `Los agentes de ${m.capsule_id} dejan de usarla. Se sigue anotando.`);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo cambiar'));
    }
  };

  const guardar = async (m) => {
    setGuardando(true);
    try {
      await memoriasDePersonaAPI.guardar(tenantId, m.capsule_id, contactId, {
        comportamiento: borrador.comportamiento,
        prosa_del_area: borrador.prosa_del_area,
        origen: 'area',
      });
      toast.success('Memoria guardada');
      setEditando(null);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo guardar'));
    } finally {
      setGuardando(false);
    }
  };

  const pedirBorrado = (m) => setConfirmacion({
    open: true,
    title: `Borrar la memoria de ${nombre} en ${m.capsule_id}`,
    description: 'Se borra el texto entero y no se recupera. Si lo que querés es que los agentes '
      + 'dejen de usarla pero se siga anotando, alcanza con el interruptor.',
    confirmText: 'Borrar la memoria',
    onConfirm: async () => {
      try {
        await memoriasDePersonaAPI.borrar(tenantId, m.capsule_id, contactId);
        toast.success('Memoria borrada');
        await cargar();
      } catch (e) {
        toast.error(formatApiError(e, 'No se pudo borrar'));
      } finally {
        setConfirmacion({ open: false });
      }
    },
  });

  return (
    <div className="mt-3 border-t border-brand-border pt-2" data-testid={`memorias-${contactId}`}>
      <button type="button" onClick={alternar}
        className="flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
        {abierto ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Brain className="w-3 h-3" />
        <span>Memoria</span>
        {cargando && <Loader2 className="w-3 h-3 animate-spin" />}
      </button>

      {abierto && (
        <div className="mt-2 space-y-3">
          <QueSeConfiguraAca
            testId="ayuda-memoria-de-persona"
            queEs="Lo que el área sabe de esta persona: con qué trabaja, sobre qué suele pedir ayuda, y un párrafo de cómo conviene abordarla."
            paraQue="Que el agente que la atienda arranque sabiendo lo que sabría alguien del área que ya la conoce, en vez de preguntar de cero cada vez."
            aQueAfecta={<>Se le pasa al agente <b>sólo</b> cuando escribe esta persona. Ningún agente puede pedir la memoria de un tercero: no existe la herramienta.</>}
            ejemplo={<>«Sus pedidos no traen el módulo ni la pantalla, así que conviene preguntarlo en la primera respuesta.»</>}
            ojo={<>Apagar el interruptor hace que los agentes <b>dejen de usarla</b>, pero el sistema <b>sigue anotando</b> lo que surge de sus casos. Para que deje de escribirse, hay que borrarla.</>}
          />

          {!cargando && !memorias.length && (
            <p className="text-xs text-zinc-500">Todavía no tiene memoria en ninguna cápsula.</p>
          )}

          {memorias.map((m) => (
            <div key={m.capsule_id} className="border border-brand-border rounded-sm p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{m.capsule_id}</p>
                  <p className="text-[11px] text-zinc-500">
                    {m.activa ? 'Se usa en las respuestas' : 'Apagada. Se sigue anotando lo que surge de sus casos.'}
                    {m.updated_at ? ` · ${String(m.updated_at).slice(0, 10)}` : ''}
                    {m.hechos?.length ? ` · ${m.hechos.length} hechos` : ''}
                    {` · ${m.caracteres_que_entran} car. al prompt`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {m.origen === 'importado' && (
                    <Badge variant="outline" className="text-[10px]">importada del buzón</Badge>
                  )}
                  <span className="text-[11px] text-zinc-600">Usar en las respuestas</span>
                  <Switch
                    checked={!!m.activa}
                    onCheckedChange={(v) => cambiarInterruptor(m, v)}
                    data-testid={`interruptor-memoria-${m.capsule_id}`}
                  />
                </div>
              </div>

              {!m.capsula_la_tiene_prendida && (
                <p className="mt-2 text-[11px] text-amber-700">
                  La cápsula {m.capsule_id} tiene las memorias apagadas: lo que elijas acá no va a
                  tener efecto hasta que se prenda en Config Cápsula.
                </p>
              )}
              {!!m.aviso && <p className="mt-2 text-[11px] text-amber-700">⚠ {m.aviso}</p>}

              {editando === m.capsule_id ? (
                <div className="mt-3 space-y-2">
                  <label className="text-[11px] text-zinc-600">Cómo conviene abordarlo</label>
                  <Textarea rows={4} value={borrador.comportamiento}
                    onChange={(e) => setBorrador((b) => ({ ...b, comportamiento: e.target.value }))}
                    placeholder="Redactado como qué conviene hacer, no como juicio." />
                  <label className="text-[11px] text-zinc-600">Con qué trabaja y sobre qué pide</label>
                  <Textarea rows={8} value={borrador.prosa_del_area}
                    onChange={(e) => setBorrador((b) => ({ ...b, prosa_del_area: e.target.value }))} />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="h-8 rounded-sm"
                      onClick={() => setEditando(null)}>Cancelar</Button>
                    <Button size="sm" className="h-8 rounded-sm" disabled={guardando}
                      onClick={() => guardar(m)}>
                      {guardando && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {!!m.comportamiento && (
                    <div className="mt-2 bg-zinc-50 border border-brand-border rounded-sm p-2">
                      <p className="text-[10px] uppercase tracking-wide text-zinc-500">Cómo conviene abordarlo</p>
                      <p className="text-xs whitespace-pre-wrap">{m.comportamiento}</p>
                    </div>
                  )}
                  {!!m.prosa_del_area && (
                    <p className="mt-2 text-xs whitespace-pre-wrap text-zinc-700">{m.prosa_del_area}</p>
                  )}
                  {!!m.prosa_aprendida && (
                    <>
                      <p className="mt-2 text-[10px] uppercase tracking-wide text-zinc-500">Aprendido de sus casos</p>
                      <p className="text-xs whitespace-pre-wrap text-zinc-600">{m.prosa_aprendida}</p>
                      {!!m.aprendizaje?.regenerada_en && (
                        <p className="text-[10px] text-zinc-400">
                          Escrito solo el {String(m.aprendizaje.regenerada_en).slice(0, 10)} a partir
                          de {m.aprendizaje.hechos_al_regenerar} hechos.
                        </p>
                      )}
                    </>
                  )}

                  {!!m.hechos?.length && (
                    <div className="mt-2">
                      <button
                        type="button"
                        className="text-[11px] text-zinc-600 underline underline-offset-2"
                        onClick={() => setHechosAbiertos((h) => ({ ...h, [m.capsule_id]: !h[m.capsule_id] }))}
                        data-testid={`ver-hechos-${m.capsule_id}`}
                      >
                        {hechosAbiertos[m.capsule_id] ? 'Ocultar' : 'Ver'} los {m.hechos.length} hechos
                        {' '}de los que salió
                      </button>
                      {hechosAbiertos[m.capsule_id] && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[10px] text-zinc-500">
                            El párrafo de arriba lo escribe el sistema <b>sólo</b> a partir de estos
                            hechos, cada vez que se juntan varios nuevos. Si dice algo que no
                            corresponde, quitá el hecho del que salió: editar el párrafo no sirve
                            porque vuelve a escribirse igual.
                          </p>
                          {m.hechos.map((h, i) => (
                            <div key={h.id || i}
                              className="flex items-start justify-between gap-2 bg-zinc-50 border border-brand-border rounded-sm px-2 py-1">
                              <div>
                                <p className="text-xs text-zinc-700">{h.texto}</p>
                                <p className="text-[10px] text-zinc-400">
                                  {h.evidencia?.ticket_name || h.evidencia?.ticket_id || 'sin caso'}
                                  {h.registrado_en ? ` · ${String(h.registrado_en).slice(0, 10)}` : ''}
                                </p>
                              </div>
                              {!!h.id && (
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0"
                                  title="Quitar este hecho"
                                  onClick={() => borrarHecho(m, h)}
                                  data-testid={`borrar-hecho-${h.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="h-8 rounded-sm" onClick={() => {
                      setBorrador({ comportamiento: m.comportamiento || '', prosa_del_area: m.prosa_del_area || '' });
                      setEditando(m.capsule_id);
                    }}>Editar</Button>
                    <Button size="sm" variant="destructive" className="h-8 rounded-sm"
                      onClick={() => pedirBorrado(m)}>
                      <Trash2 className="w-3 h-3 mr-1" />Borrar
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmActionDialog
        open={confirmacion.open}
        onOpenChange={(v) => setConfirmacion((c) => ({ ...c, open: v }))}
        title={confirmacion.title}
        description={confirmacion.description}
        confirmText={confirmacion.confirmText}
        onConfirm={confirmacion.onConfirm}
      />
    </div>
  );
}
